import React, { useState } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { confirmPayment, handleApiError } from '../services/api';
import { getPackageDetails } from '../config/stripe';

const PaymentForm = ({ 
  sessionId, 
  packageId = 'call_with_concierge', 
  customerInfo,
  formData,
  serviceInfo = null,
  onPaymentSuccess,
  onPaymentError,
  onPaymentProcessing,
  clientSecret,
  paymentIntentId
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');

  // Get package details or use service info
  console.log("💳 PaymentForm received serviceInfo:", serviceInfo);
  
  const packageDetails = serviceInfo && serviceInfo.price
    ? { 
        name: serviceInfo.name,
        displayPrice: `$${parseFloat(serviceInfo.price).toFixed(2)}` 
      }
    : getPackageDetails(packageId);
  
  console.log("💳 PaymentForm packageDetails:", packageDetails);

  // Handle payment submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    if (!clientSecret) {
      setError('Payment setup failed. Please refresh and try again.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setProcessingStatus('Processing payment...');
    
    if (onPaymentProcessing) {
      onPaymentProcessing(true);
    }

    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href, // Fallback URL
        },
        redirect: 'if_required' // Only redirect if required (e.g., 3D Secure)
      });

      if (stripeError) {
        throw stripeError;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        setProcessingStatus('Confirming with server...');
        
        // Confirm payment with our backend
        const confirmationResponse = await confirmPayment(
          sessionId, 
          paymentIntent.id, 
          formData
        );

        setProcessingStatus('Payment completed!');

        // Call success callback
        if (onPaymentSuccess) {
          onPaymentSuccess({
            paymentIntent,
            confirmation: confirmationResponse,
            receiptUrl: confirmationResponse.receipt_url
          });
        }

      } else {
        throw new Error(`Payment failed with status: ${paymentIntent?.status}`);
      }

    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      
      if (onPaymentError) {
        onPaymentError(err, errorMessage);
      }
    } finally {
      setIsLoading(false);
      setProcessingStatus('');
      
      if (onPaymentProcessing) {
        onPaymentProcessing(false);
      }
    }
  };

  // Loading state while Stripe loads
  if (!stripe || !elements) {
    return (
      <div className="nc-payment-loading">
        <div className="nc-payment-spinner"></div>
        <p>Loading payment form...</p>
      </div>
    );
  }


  return (
    <div className="nc-payment-form">
      {/* Payment Summary */}
      <div className="nc-payment-summary">
        <div className="nc-payment-item">
          <span className="nc-payment-label">{serviceInfo ? 'Service:' : 'Selected Package:'}</span>
          <span className="nc-payment-value">{packageDetails.name || 'Call With Concierge'}</span>
        </div>
        <div className="nc-payment-item">
          <span className="nc-payment-label">Customer:</span>
          <span className="nc-payment-value">{customerInfo.name}</span>
        </div>
        <div className="nc-payment-item">
          <span className="nc-payment-label">Email:</span>
          <span className="nc-payment-value">{customerInfo.email}</span>
        </div>
        <div className="nc-payment-item total">
          <span className="nc-payment-label">Total Amount:</span>
          <span className="nc-payment-value">{packageDetails.displayPrice}</span>
        </div>
      </div>

      {/* Processing Status */}
      {processingStatus && (
        <div className="nc-processing-status">
          <div className="nc-payment-spinner small"></div>
          <span>{processingStatus}</span>
        </div>
      )}

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="nc-stripe-form">
        <div className="nc-payment-element-container">
          <PaymentElement 
            id="nc-payment-element"
            options={{
              layout: "tabs"
            }}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="nc-payment-error-message">
            <span className="nc-error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button 
          type="submit" 
          disabled={!stripe || !clientSecret || isLoading}
          className={`nc-btn nc-btn-primary nc-payment-submit ${isLoading ? 'loading' : ''}`}
        >
          {isLoading ? (
            <>
              <div className="nc-payment-spinner small"></div>
              Processing...
            </>
          ) : (
            <>
              Complete Payment {packageDetails.displayPrice}
              <i className="fas fa-lock" style={{marginLeft: '8px'}}></i>
            </>
          )}
        </button>

        {/* Security Notice */}
        <div className="nc-security-notice">
          <i className="fas fa-shield-alt"></i>
          <span>Your payment information is secure and encrypted</span>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
